/**
 * A supported-meal row on My Orders, marked by the green stripe and tag.
 *
 * Shows both dates explicitly. The artwork showed a single unlabelled date
 * (gap G-16), which left donors unable to tell the donation date from the
 * distribution date.
 */

import { memo } from 'react';
import { Image, Pressable, StyleSheet, View } from 'react-native';

import { formatDate, formatMoney, formatPeople, type DonationSummary } from '@mfa/domain';
import { mealImageSource } from '../../assets';
import { colors, radius, shadow, space } from '../../theme';
import { OrderStatusBadge } from '../common/Badge';
import { Glyph } from '../common/Icon';
import { Text } from '../common/Text';
import { ORDER_TYPE_LOOK, OrderTypeTag } from '../orders/OrderTypeTag';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';

export interface DonationListItemProps {
  donation: DonationSummary;
  onPress: (donation: DonationSummary) => void;
  testID?: string;
}

export const DonationListItem = memo(function DonationListItem({
  donation,
  onPress,
  testID,
}: DonationListItemProps) {
  const t = useT();
  const pick = useLocalized();
  return (
    <Pressable
      testID={testID ?? `donation-${donation.publicReference}`}
      onPress={() => onPress(donation)}
      accessibilityRole="button"
      accessibilityLabel={`${t('Meal Supported')} ${donation.publicReference}, ${pick(donation.meal.name, donation.meal.nameUr)}, ${formatPeople(
        donation.peopleCount,
      )}, ${formatMoney(donation.totalAmount)}`}
      style={({ pressed }) => [styles.card, pressed && styles.pressed]}
    >
      <View style={styles.top}>
        {(() => {
          const source = mealImageSource(donation.meal.imageUrl, donation.meal.name);
          return source ? (
            <Image source={source} style={styles.image} accessibilityIgnoresInvertColors />
          ) : (
            <View style={[styles.image, styles.imageFallback]} />
          );
        })()}

        <View style={styles.body}>
          <View style={styles.headRow}>
            <OrderTypeTag type="DONATION" />
            <Text variant="label" tone="secondary">
              #{donation.publicReference}
            </Text>
          </View>
          <Text variant="cardTitle" numberOfLines={2}>
            {pick(donation.meal.name, donation.meal.nameUr)}
          </Text>
          <View style={styles.statusRow}>
            <Text variant="caption" tone="secondary">
              {formatPeople(donation.peopleCount)}
            </Text>
            <OrderStatusBadge status={donation.orderStatus} />
          </View>
        </View>
      </View>

      <View style={styles.footer}>
        <View style={styles.dates}>
          <View style={styles.dateRow}>
            <Glyph name="calendar" size={12} />
            <Text variant="caption" tone="tertiary">
              {t('Distribution')} {formatDate(donation.distributionDate)}
            </Text>
          </View>
          <Text variant="caption" tone="tertiary">
            {t('Supported')} {formatDate(donation.donationDate)}
          </Text>
        </View>
        <Text variant="amountSmall" tone="brand">
          {formatMoney(donation.totalAmount)}
        </Text>
      </View>
    </Pressable>
  );
});

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.bg.surface,
    borderRadius: radius.lg,
    borderLeftWidth: space.xs,
    borderLeftColor: ORDER_TYPE_LOOK.DONATION.accent,
    padding: space.md,
    gap: space.md,
    ...shadow('card'),
  },
  pressed: {
    backgroundColor: colors.bg.surfaceSunken,
  },
  top: {
    flexDirection: 'row',
    gap: space.md,
  },
  image: {
    width: 72,
    height: 72,
    borderRadius: radius.md,
    resizeMode: 'cover',
  },
  imageFallback: {
    backgroundColor: colors.bg.surfaceSunken,
  },
  body: {
    flex: 1,
    gap: space.xxs,
  },
  headRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.xs,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: space.xs,
    gap: space.sm,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border.subtle,
    paddingTop: space.md,
    gap: space.sm,
  },
  dates: {
    gap: space.xxs,
  },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
  },
});
